import { useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { t } from '@/i18n';
import { useUsersApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { USER_ROLE_NAMES } from '@ip/shared-types';
import type { CompanyListItem, UserRoleName } from '@ip/shared-types';

interface AddCompanyAccessProps {
  userId: string;
  /** Manageable companies the user has no roles/grants in yet. */
  companies: CompanyListItem[];
  onChanged: () => void;
}

/**
 * "Add company" flow for the permissions drawer: one popover, two steps —
 * search-pick a company, then pick the initial role. The role is saved
 * immediately, so the new company card appears from server state and no
 * transient local card is needed.
 */
export function AddCompanyAccess({ userId, companies, onChanged }: AddCompanyAccessProps) {
  const { addRole } = useUsersApi();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<CompanyListItem | null>(null);
  const [saving, setSaving] = useState(false);

  const handlePickRole = async (role: UserRoleName) => {
    if (!selected) return;
    try {
      setSaving(true);
      await addRole(userId, { role, companyNip: selected.nip });
      toast.success(t('users.drawer.roleAdded'));
      setOpen(false);
      setSelected(null);
      onChanged();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('users.errors.addRoleFailed'));
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setSelected(null);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-full border-dashed"
          disabled={companies.length === 0}
        >
          <Plus className="size-4" />
          {t('users.drawer.addCompany')}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        {selected === null ? (
          <Command>
            <CommandInput placeholder={t('users.drawer.searchCompany')} />
            <CommandList>
              <CommandEmpty>{t('users.drawer.noCompanyResults')}</CommandEmpty>
              <CommandGroup>
                {companies.map((company) => (
                  <CommandItem
                    key={company.nip}
                    value={`${company.name ?? ''} ${company.nip}`}
                    onSelect={() => setSelected(company)}
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm">{company.name || company.nip}</p>
                      <p className="text-muted-foreground font-mono text-xs">NIP {company.nip}</p>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        ) : (
          <div className="p-1">
            <div className="px-2 py-1.5">
              <p className="truncate text-sm font-medium">{selected.name || selected.nip}</p>
              <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
                {t('users.drawer.pickRole')}
                {saving && <Loader2 className="size-3 animate-spin" />}
              </p>
            </div>
            {USER_ROLE_NAMES.map((role) => (
              <button
                key={role}
                type="button"
                disabled={saving}
                onClick={() => handlePickRole(role)}
                className="hover:bg-accent hover:text-accent-foreground flex w-full items-center rounded-sm px-2 py-1.5 text-sm disabled:opacity-50"
              >
                {t(`users.roleNames.${role}`)}
              </button>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
